import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStamps } from '../hooks/useStamps';
import { useHaptic } from '../hooks/useHaptic';
import { stampsAPI } from '../services/api';
import { Upload, Loader2, Image as ImageIcon, Trash2, Plus } from 'lucide-react';
import './StampsPage.css';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

function StampsPage() {
  const navigate = useNavigate();
  const { stamps, loading, refetch, deleteStamp } = useStamps();
  const haptic = useHaptic();
  const [activeTab, setActiveTab] = useState('signature'); // 'signature' или 'stamp'
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');

  const signatures = stamps.filter(s => s.type === 'signature');
  const stampsList = stamps.filter(s => s.type === 'stamp');

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    haptic.buttonPress();

    // Проверка типа файла
    if (!file.type.startsWith('image/')) {
      haptic.warning();
      if (window.Telegram?.WebApp?.showAlert) {
        window.Telegram.WebApp.showAlert('Пожалуйста, выберите изображение');
      } else {
        alert('Пожалуйста, выберите изображение');
      }
      return;
    }

    // Проверка размера файла (10MB)
    if (file.size > 10 * 1024 * 1024) {
      haptic.warning();
      if (window.Telegram?.WebApp?.showAlert) {
        window.Telegram.WebApp.showAlert('Размер файла не должен превышать 10MB');
      } else {
        alert('Размер файла не должен превышать 10MB');
      }
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadStatus('Загружаем изображение...');

    try {
      haptic.fileUpload();
      
      // Симулируем прогресс загрузки с разными статусами (более плавно)
      const statuses = [
        { progress: 15, message: 'Загружаем изображение...', delay: 600 },
        { progress: 35, message: 'Обрабатываем через AI...', delay: 800 },
        { progress: 55, message: 'Удаляем фон...', delay: 700 },
        { progress: 75, message: 'Оптимизируем качество...', delay: 600 },
        { progress: 88, message: 'Почти готово...', delay: 500 }
      ];
      
      let currentStatusIndex = 0;
      
      const updateStatus = () => {
        if (currentStatusIndex < statuses.length) {
          const status = statuses[currentStatusIndex];
          setUploadProgress(status.progress);
          setUploadStatus(status.message);
          currentStatusIndex++;
          
          if (currentStatusIndex < statuses.length) {
            setTimeout(updateStatus, statuses[currentStatusIndex - 1].delay);
          }
        }
      };
      
      // Начинаем обновление статусов
      setTimeout(updateStatus, statuses[0].delay);

      const response = await stampsAPI.upload(file, activeTab);
      
      setUploadProgress(100);
      setUploadStatus('Файл успешно обработан!');
      
      // Даем время показать 100% прогресс
      await new Promise(resolve => setTimeout(resolve, 600));
      
      // Предзагружаем medium версию для быстрой вставки в документ
      if (response.data && response.data.stamp) {
        const stampId = response.data.stamp.id;
        const mediumImageUrl = `${API_URL}/stamps/${stampId}/medium?initData=${encodeURIComponent(window.Telegram?.WebApp?.initData || '')}`;
        
        // Создаем Image объект для предзагрузки
        const preloadImg = new Image();
        preloadImg.src = mediumImageUrl;
        
        console.log(`🔄 Предзагрузка medium версии для печати ${stampId}`);
      }
      
      haptic.success();
      // Показываем уведомление об успехе
      if (window.Telegram?.WebApp?.showPopup) {
        window.Telegram.WebApp.showPopup({
          title: "Готово",
          message: `${activeTab === 'signature' ? 'Подпись' : 'Печать'} успешно загружена!`,
          buttons: [{ type: "ok" }]
        });
      }

      // Обновляем список
      await refetch();
    } catch (error) {
      console.error('Ошибка загрузки:', error);
      haptic.error();
      
      const errorMessage = error.response?.data?.error || 'Не удалось загрузить изображение';
      
      if (window.Telegram?.WebApp?.showAlert) {
        window.Telegram.WebApp.showAlert(errorMessage);
      } else {
        alert(errorMessage);
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadStatus('');
      // Сбрасываем input
      event.target.value = '';
    }
  };

  const handleDelete = async (stamp) => {
    haptic.buttonPressCritical();
    const confirmDelete = window.Telegram?.WebApp?.showConfirm 
      ? await new Promise(resolve => {
          window.Telegram.WebApp.showConfirm(
            `Удалить ${stamp.type === 'signature' ? 'подпись' : 'печать'}?`,
            resolve
          );
        })
      : window.confirm(`Удалить ${stamp.type === 'signature' ? 'подпись' : 'печать'}?`);

    if (confirmDelete) {
      try {
        haptic.delete();
        await deleteStamp(stamp.id);
        haptic.success();
        
        if (window.Telegram?.WebApp?.showPopup) {
          window.Telegram.WebApp.showPopup({
            title: "Готово",
            message: `${stamp.type === 'signature' ? 'Подпись' : 'Печать'} удалена`,
            buttons: [{ type: "ok" }]
          });
        }
      } catch (error) {
        console.error('Ошибка удаления:', error);
        haptic.error();
        
        if (window.Telegram?.WebApp?.showAlert) {
          window.Telegram.WebApp.showAlert('Не удалось удалить');
        } else {
          alert('Не удалось удалить');
        }
      }
    }
  };

  const currentStamps = activeTab === 'signature' ? signatures : stampsList;

  if (loading && stamps.length === 0) {
    return (
      <div className="container flex flex-col items-center justify-center" style={{ minHeight: '100vh' }}>
        <Loader2 className="loader-icon" />
        <p className="mt-16">Загрузка...</p>
      </div>
    );
  }

  return (
    <div className="stamps-page container">
      <div className="page-header animate-fade-in">
        <h1>Управление подписями и печатями</h1>
        <p className="page-subtitle">
          Добавляйте и удаляйте подписи и печати для использования в документах
        </p>
      </div>

      {/* Табы */}
      <div className="tabs animate-slide-up animate-delay-100">
        <button
          className={`tab ${activeTab === 'signature' ? 'active' : ''}`}
          onClick={() => {
            haptic.toggle();
            setActiveTab('signature');
          }}
        >
          <ImageIcon size={20} />
          Подписи ({signatures.length})
        </button>
        <button
          className={`tab ${activeTab === 'stamp' ? 'active' : ''}`}
          onClick={() => {
            haptic.toggle();
            setActiveTab('stamp');
          }}
        >
          <ImageIcon size={20} />
          Печати ({stampsList.length})
        </button>
      </div>

      {/* Кнопка загрузки */}
      <div className="upload-section animate-slide-up animate-delay-200">
        <input
          type="file"
          id="file-upload"
          accept="image/*"
          onChange={handleFileUpload}
          disabled={uploading}
          style={{ display: 'none' }}
        />
        <label htmlFor="file-upload" className={`upload-button ${uploading ? 'disabled' : ''}`}>
          {uploading ? (
            <>
              <Loader2 size={20} className="animate-spin" />
              {uploadStatus}
            </>
          ) : (
            <>
              <Plus size={20} />
              Добавить {activeTab === 'signature' ? 'подпись' : 'печать'}
            </>
          )}
        </label>
        
        {/* Прогресс-бар */}
        {uploading && (
          <div className="upload-progress-container">
            <div 
              className="upload-progress-bar" 
              style={{ width: `${uploadProgress}%` }}
            >
              <div className="upload-progress-shimmer"></div>
            </div>
          </div>
        )}
      </div>

      {/* Инструкция */}
      <div className="info-box animate-fade-in animate-delay-300">
        <Upload size={20} />
        <div>
          <p className="info-title">Как добавить {activeTab === 'signature' ? 'подпись' : 'печать'}?</p>
          <p className="info-text">
            Нажмите кнопку выше и выберите изображение. Изображение будет обработано с помощью AI.
          </p>
        </div>
      </div>

      {/* Список печатей/подписей */}
      {currentStamps.length === 0 ? (
        <div className="empty-state animate-scale-in animate-delay-400">
          <ImageIcon size={48} className="empty-icon" />
          <h3>Нет {activeTab === 'signature' ? 'подписей' : 'печатей'}</h3>
          <p>Добавьте {activeTab === 'signature' ? 'подпись' : 'печать'}, чтобы начать подписывать документы</p>
        </div>
      ) : (
        <div className="stamps-grid">
          {currentStamps.map((stamp, index) => {
            const imageUrl = `${API_URL}/stamps/${stamp.id}/thumbnail?initData=${encodeURIComponent(window.Telegram?.WebApp?.initData || '')}`;
            
            return (
              <div 
                key={stamp.id} 
                className="stamp-card stagger-item"
                style={{ animationDelay: `${0.4 + index * 0.1}s` }}
              >
                <div className="stamp-image-container">
                  <img 
                    src={imageUrl}
                    alt={stamp.original_name}
                    className="stamp-image"
                    loading="lazy"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const placeholder = e.target.parentElement.querySelector('.stamp-placeholder');
                      if (placeholder) {
                        placeholder.style.display = 'flex';
                      }
                    }}
                  />
                  <div className="stamp-placeholder" style={{ display: 'none' }}>
                    <ImageIcon size={32} />
                  </div>
                </div>
                <div className="stamp-info">
                  <span className="stamp-name">{stamp.original_name}</span>
                  <span className="stamp-date">
                    {new Date(stamp.created_at).toLocaleDateString('ru-RU')}
                  </span>
                </div>
                <button
                  className="delete-stamp-button"
                  onClick={() => handleDelete(stamp)}
                  title="Удалить"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default StampsPage;

